import HelpIcon from 'components/icons/HelpIcon';
import { NOT_ASSIGNED } from 'constants/constants';
import warningIcon from 'images/icons/svg/warning.svg';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Select, { SingleValue } from 'react-select';
import AuthService from 'services/AuthService';
import { ActionState, EPDModel, EPDStatus, LicenseType } from 'services/EpdClient';
import styled, { useTheme } from 'styled-components';
import { A } from 'styles/Styles.styled';
import { Theme } from 'styles/theme';
import { dateToIsoString } from 'util/utils';
import IsoDateInput from '../../components/wizard/IsoDateInput';
import AdminDownloadIlcdEpd from './AdminDownloadIlcdEpd';
import { Validation } from './Validation';
import { FixedLabel, FixedLabelTop, FixedRow, SelectWrapper } from './styles';
import { WizardStepValidationSchema } from './types';

type LicenseTypeOption = {
  value: LicenseType;
  label: string;
};

const licenseTypeOptions: LicenseTypeOption[] = [
  { value: LicenseType.Missing, label: 'Missing' },
  { value: LicenseType.FreeForAll, label: 'Free of charge for all users and uses' },
  { value: LicenseType.FreeForMembersOnly, label: 'Free of charge for members only' },
  { value: LicenseType.FreeForSome, label: 'Free of charge for some user types or use types' },
];

const WizardStep4: React.FunctionComponent<{
  epd?: EPDModel;
  onChangeEpd?: any;
  registerConsent: (val: boolean) => void;
  validation: WizardStepValidationSchema;
}> = ({ epd, onChangeEpd, registerConsent, validation }) => {
  const { t } = useTranslation();
  const theme = useTheme() as Theme;
  const isAdmin = AuthService.isAdmin();

  let statusOptions: any[] = [];
  for (let s in EPDStatus) {
    statusOptions.push({ value: s, label: EPDStatus[s as keyof typeof EPDStatus] });
  }

  const changeLicenseType = (license: SingleValue<LicenseTypeOption>) => {
    if (license) {
      onChangeEpd('LicenseType', license.value);
    }
  };

  return (
    <div>
      {epd?.versionId && epd.epdState?.downloadIlcdEpdBundle === ActionState.Enabled && (
        <AdminDownloadIlcdEpd epdVersionId={epd.versionId} />
      )}
      <FixedRow state={epd?.firstPublishedVersionDateState.actionState}>
        <FixedLabel>
          First version date
          <HelpIcon content="Version date of the first published EPD version." />
          <Validation validation={validation} epd={epd} field="firstPublishedVersionDate" />
        </FixedLabel>
        {epd?.firstPublishedVersionDateState.actionState !== ActionState.Enabled && !isAdmin ? (
          <EPDOwnerDatesWrapper>{dateToIsoString(epd?.firstPublishedVersionDate)}</EPDOwnerDatesWrapper>
        ) : (
          <IsoDateInput
            value={dateToIsoString(epd?.firstPublishedVersionDate)}
            type={'date'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              e.currentTarget.value !== dateToIsoString(epd?.firstPublishedVersionDate) &&
              onChangeEpd('firstPublishedVersionDate', new Date(e.currentTarget.value))
            }
            disabled={epd?.firstPublishedVersionDateState.actionState !== ActionState.Enabled}
          />
        )}
      </FixedRow>
      <FixedRow state={epd?.versionDateState.actionState}>
        <FixedLabel>
          {t('epdWizard.wizardStep4.properties.versionDate')}
          <HelpIcon content={t('epdWizard.wizardStep4.tooltips.versionDate')} />
          <Validation validation={validation} epd={epd} field="versionDate" />
        </FixedLabel>
        {epd?.versionDateState.actionState !== ActionState.Enabled && !isAdmin ? (
          <EPDOwnerDatesWrapper>{dateToIsoString(epd?.versionDate)}</EPDOwnerDatesWrapper>
        ) : (
          <IsoDateInput
            value={dateToIsoString(epd?.versionDate)}
            type={'date'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              e.currentTarget.value !== dateToIsoString(epd?.versionDate) &&
              onChangeEpd('versionDate', new Date(e.currentTarget.value))
            }
            disabled={epd?.versionDateState.actionState !== ActionState.Enabled}
          />
        )}
      </FixedRow>
      <FixedRow state={epd?.validityDateState.actionState}>
        <FixedLabel>
          {t('epdWizard.wizardStep4.properties.validityDate.title')}
          <HelpIcon content={t('epdWizard.wizardStep4.tooltips.validityDate')} />
          <Validation validation={validation} epd={epd} field="validityDate" />
        </FixedLabel>
        {epd?.validityDateState.actionState !== ActionState.Enabled && !isAdmin ? (
          <EPDOwnerDatesWrapper>{dateToIsoString(epd?.validityDate)}</EPDOwnerDatesWrapper>
        ) : (
          <IsoDateInput
            value={dateToIsoString(epd?.validityDate)}
            type={'date'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              e.currentTarget.value !== dateToIsoString(epd?.validityDate) &&
                onChangeEpd('validityDate', new Date(e.currentTarget.value));
            }}
            disabled={epd?.validityDateState.actionState !== ActionState.Enabled}
          />
        )}
      </FixedRow>
      <FixedRow state={epd?.epdOwnerState.actionState}>
        <FixedLabel>
          {t('epdWizard.wizardStep4.properties.epdOwner')}
          <HelpIcon content={t('epdWizard.wizardStep4.tooltips.contact')} />
        </FixedLabel>
        <Value>
          {epd?.epdOwner?.companyName} <br />
          {epd?.epdOwner?.contactName} {epd?.epdOwner?.contactEmail}
        </Value>
      </FixedRow>
      <FixedRow state={epd?.epdOwnerState.actionState}>
        <FixedLabel>
          {t('epdWizard.wizardStep4.properties.epdOwnerHomeCountry')}
          <HelpIcon content={t('epdWizard.wizardStep4.tooltips.homeCountry')} />
        </FixedLabel>
        <Value>{epd?.epdOwner?.companyHomeCountry}</Value>
      </FixedRow>
      <FixedRow state={epd?.isProtectedByCopyrightState.actionState}>
        <FixedLabel>
          {t('epdWizard.wizardStep4.properties.copyRight')}
          <HelpIcon content={t('epdWizard.wizardStep4.tooltips.copyright')} />
        </FixedLabel>
        <Value>
          {epd?.isProtectedByCopyright
            ? t('epdWizard.wizardStep4.properties.protected')
            : t('epdWizard.wizardStep4.properties.unprotected')}
        </Value>
      </FixedRow>
      <FixedRow state={epd?.licenseTypeState.actionState}>
        <FixedLabel>
          {t('epdWizard.wizardStep4.properties.licenseType')}
          <HelpIcon content={t('epdWizard.wizardStep4.tooltips.licenseType')} />
        </FixedLabel>
        {epd?.licenseTypeState.actionState === ActionState.Enabled ? (
          <SelectWrapper>
            <Select
              onChange={(e) => changeLicenseType(e)}
              options={licenseTypeOptions}
              styles={customSelectStyles}
              value={licenseTypeOptions.find((l) => l.value === epd?.licenseType)}
            />
          </SelectWrapper>
        ) : (
          <Value>{licenseTypeOptions.find((l) => l.value === epd?.licenseType)?.label ?? NOT_ASSIGNED}</Value>
        )}
      </FixedRow>
      <FixedRow state={epd?.statusState.actionState}>
        <FixedLabel>{t('epdWizard.wizardStep4.properties.status')}</FixedLabel>
        <Value>{epd?.status}</Value>
      </FixedRow>
      {(epd?.epdState?.sendForPublication !== ActionState.Hidden ||
        epd?.epdState?.sendForPublicationEditorial !== ActionState.Hidden) && (
        <FixedRow>
          <FixedLabelTop>{t('epdWizard.wizardStep4.properties.confirmation.title')}</FixedLabelTop>
          <div>
            <InfoBox style={{ borderLeft: 'solid 3px green' }}>
              <img src={warningIcon} alt="confirmation" style={{ height: '35px', marginRight: '1rem', marginTop: '1rem' }} />
              <div>
                <p>{t('epdWizard.wizardStep4.properties.confirmation.info1')}</p>
                <p>{t('epdWizard.wizardStep4.properties.confirmation.info2')}</p>
                <p>
                  <strong>{t('epdWizard.wizardStep4.properties.confirmation.infoFees')}</strong>
                </p>
                <p>
                  <A target="_blank" rel="noopener noreferrer" href={theme.serviceAgreementUrl}>
                    {t('epdWizard.wizardStep4.properties.confirmation.serviceAgreement')}
                  </A>
                </p>
                <p>
                  <label style={{ fontWeight: 600, fontSize: '18px' }}>
                    <Input
                      type="checkbox"
                      style={{ margin: '0.5rem 10px 1rem 0' }}
                      defaultChecked={epd?.serviceAgreementStatus ?? false}
                      disabled={epd?.epdState?.sendForPublication === ActionState.Disabled}
                      onChange={(e) => {
                        registerConsent(e.target.checked);
                      }}
                    />{' '}
                    {t('epdWizard.wizardStep4.properties.confirmation.iAgree')}
                  </label>
                </p>
              </div>
            </InfoBox>
          </div>
        </FixedRow>
      )}
    </div>
  );
};

export const customSelectStyles = {
  menu: (styles: any) => ({ ...styles }),
  control: (styles: any) => ({
    ...styles,
    borderRadius: 0,
    borderColor: 'gray',
    color: 'black',
    fontSize: '14px',
    minHeight: '49px',
    margin: '0.5rem 0 ',
    minWidth: '10rem',
  }),
  multiValueLabel: (styles: any) => ({
    ...styles,
    fontSize: '100%',
  }),
};

const Value = styled.em`
  margin: 0.5rem 0;
`;

export const InfoBox = styled.div`
  display: flex;
  padding: 1rem;
  border-left: solid 3px green;
  margin: 0.5rem 0;
  background-color: #f3f3f3;
`;

const EPDOwnerDatesWrapper = styled.em`
  align-self: center;
`;

const Input = styled.input`
  -ms-transform: scale(2);
  -moz-transform: scale(2);
  -webkit-transform: scale(2);
  -o-transform: scale(2);
  transform: scale(2);
  padding: 10px;
`;

export default WizardStep4;
